import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { cn } from "@/lib/utils";

export interface LinearProgressProps extends ComponentPropsWithoutRef<"div"> {
  /**
   * Whether the progress indicator is visible.
   * @default true
   */
  visible?: boolean;
}

/**
 * Linear progress indicator for background sync operations.
 * 
 * Displays a thin horizontal progress bar with an indeterminate animation
 * (continuous left-to-right sweep). Used to signal background operations
 * like reconnection or session sync without blocking the UI.
 */
export const LinearProgress = forwardRef<HTMLDivElement, LinearProgressProps>(
  ({ className, visible = true, ...props }, ref) => {
    if (!visible) return null;

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-label="Syncing…"
        aria-valuenow={undefined}
        aria-valuemin={0}
        aria-valuemax={100}
        className={cn(
          "relative h-[3px] w-full overflow-hidden bg-white",
          className,
        )}
        {...props}
      >
        <div
          className="absolute h-full w-full bg-[#DB3725]"
          style={{
            animation: "progress-sweep 1.5s ease-in-out infinite",
          }}
        />
      </div>
    );
  },
);

LinearProgress.displayName = "LinearProgress";
